import { RotateCw, BookOpen, Sparkles, Flame } from "lucide-react";
import { readStreak } from "@/lib/streak";

/**
 * "Deep Work Complete" completion card — the reader finished their active queue.
 *
 * Actions:
 *   1. Launch Revision Blitz → parent reloads with `mode=revision` (Smart 10-card burst).
 *   2. Review All Read Cards → parent switches to `mode=history` (full chronological archive).
 *
 * The "Browse Subject Vault" button is intentionally removed — the sticky
 * category pills at the top of the feed already own that job.
 */
export default function CaughtUpCard({ onRevision, onReviewHistory, showRevision = true }) {
  const streak = (() => {
    try { return readStreak(); }
    catch (err) { void err; return { current: 0, best: 0 }; }
  })();
  const streakActive = (streak?.current || 0) > 0;

  return (
    <div
      data-testid="feed-caught-up"
      className="w-full h-full max-w-[720px] mx-auto flex items-center justify-center px-2 sm:px-4"
    >
      <div className="relative w-full rounded-2xl bg-[#1A2232] border border-[#26334D] overflow-hidden shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 h-24 pointer-events-none"
          style={{ background: "linear-gradient(180deg, rgba(242,189,104,0.14) 0%, rgba(242,189,104,0) 100%)" }}
        />
        <div className="relative p-6 sm:p-8">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#F2BD68]/40 bg-[#F2BD68]/10 px-3 py-1 text-[10.5px] font-semibold tracking-[0.16em] uppercase text-[#F2BD68]">
            <Sparkles className="h-3 w-3" aria-hidden />
            Deep Work Complete
          </span>

          <h3
            className="mt-4 font-semibold tracking-tight text-[#F8FAFC] break-words"
            style={{ fontSize: "clamp(20px, 5.4vw, 28px)", lineHeight: 1.25 }}
          >
            <span aria-hidden className="mr-1">⚡</span>
            Deep work complete. Brain cells upgraded.
          </h3>

          <p
            className="mt-3 text-[#94A3B8]"
            style={{ fontSize: "clamp(13.5px, 3.7vw, 15px)", lineHeight: 1.65 }}
          >
            You&rsquo;ve cleared your active recall queue. The syllabus looks a little smaller now.
          </p>

          {streakActive && (
            <div
              data-testid="feed-caught-up-streak"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#E85A2A]/45 bg-[#E85A2A]/12 px-3 py-1.5 text-[12px] font-semibold text-[#E85A2A]"
            >
              <Flame className="h-3.5 w-3.5" aria-hidden />
              {streak.current}-Day Streak Active
              {streak.best > streak.current && (
                <span className="text-[10.5px] text-[#F2BD68]/80 font-medium">· best {streak.best}</span>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-col gap-3 w-full">
            {showRevision && (
              <button
                type="button"
                onClick={onRevision}
                data-testid="feed-launch-revision"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#E85A2A] text-[#F8FAFC] px-4 py-3 text-sm font-semibold hover:bg-[#C94A1D] active:scale-[0.98] transition-all shadow-[0_6px_18px_rgba(232,90,42,0.35)]"
              >
                <RotateCw className="h-4 w-4" aria-hidden />
                <span aria-hidden>⚡</span> Launch Revision Blitz
              </button>
            )}
            <button
              type="button"
              onClick={onReviewHistory}
              data-testid="feed-review-history"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#26334D] bg-[#111622] text-[#F8FAFC] px-4 py-3 text-sm font-semibold hover:bg-[#232E45] transition-colors"
            >
              <BookOpen className="h-4 w-4" aria-hidden />
              <span aria-hidden>📖</span> Review All Read Cards
            </button>
          </div>

          <p className="mt-5 text-[11px] text-[#64748B]" style={{ lineHeight: 1.6 }}>
            Revision Blitz surfaces a smart 10-card micro-burst weighted toward your weak spots and cards fading from memory.
          </p>
        </div>
      </div>
    </div>
  );
}
